import React from 'react';
import useProfileTransferRequest from '../../../../../../hooks/api/useProfileTransferRequest';
import useInboundTransferRequests from '../../../../../../hooks/api/useInboundTransferRequests';
import { useIsProfileOwner } from '../../../../../v2BaseComponents/Permissions/hooks';
import { getIntl } from '../../../../../../utils/intl';
import getName from '../../../../../../utils/getName';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import LargeAlertPaper from '../../../../../v2BaseComponents/LargeAlertPaper';

const intl = getIntl('pendingOwnershipTransferAlert', {
    observerTitle: 'Profile Ownership Transfer In-Progress',
    observerCollaboratorTransferMessage: `Profile administration is in the process of being transferred from {fromUserFullName} to the collaborator {toUserFullName}.`,
    observerNonCollaboratorTransferMessage: `Profile administration is in the process of being transferred from {fromUserFullName} to user with the email "{toUserEmail}".`,
    recipientTitle: 'Profile Ownership Transfer Request',
    recipientMessage: `You have been requested to take over profile administration from {fromUserFullName}.`,
    senderTitle: 'Active Ownership Transfer Request',
    senderCollaboratorMessage: `You have requested to transfer profile administration to {toUserFullName}.`,
    senderNonCollaboratorMessage: `You have requested to transfer profile administration to user with the email "{toUserEmail}".`,
    manageRequest: 'Manage Transfer Request',
    respondToRequest: 'Respond to Transfer Request',
});

export type PendingOwnershipTransferAlertOwnProps = {
    profileId: string;
};

export type PendingOwnershipTransferAlertProps = PendingOwnershipTransferAlertOwnProps;

const PendingOwnershipTransferAlert: React.FC<PendingOwnershipTransferAlertProps> = ({ profileId }) => {
    const navigation = useProfilesTabNavigation();
    const { isLoadingProfileTransferRequest, profileTransferRequest } = useProfileTransferRequest({
        profileId,
    });
    const { isLoadingInboundTransferRequests, inboundTransferRequests } = useInboundTransferRequests();
    const isProfileOwner = useIsProfileOwner(profileId);

    if (
        isLoadingProfileTransferRequest ||
        isLoadingInboundTransferRequests ||
        profileTransferRequest.status === 'no-pending-transfer-request'
    )
        return null;

    const userRecipientTransferRequest = inboundTransferRequests.find(
        (request) => request.profile.medicalProfileId === profileId,
    );

    if (isProfileOwner) {
        return (
            <LargeAlertPaper
                icon="HorizontalArrows2"
                title={intl('senderTitle')}
                message={
                    profileTransferRequest.status === 'pending-collaborator-transfer-request'
                        ? intl('senderCollaboratorMessage', {
                              toUserFullName: getName(profileTransferRequest.transferToUser),
                          })
                        : intl('senderNonCollaboratorMessage', {
                              toUserEmail: profileTransferRequest.transferToEmail,
                          })
                }
                actionItems={[
                    {
                        title: intl('manageRequest'),
                        onPress: () =>
                            navigation.navigate('transfer-request', {
                                profileId,
                            }),
                        icon: 'Settings',
                    },
                ]}
            />
        );
    } else if (userRecipientTransferRequest) {
        return (
            <LargeAlertPaper
                icon="HorizontalArrows2"
                testID="transfer-request-alert"
                title={intl('recipientTitle')}
                message={intl('recipientMessage', {
                    fromUserFullName: getName(profileTransferRequest.transferFromUser),
                })}
                actionItems={[
                    {
                        testID: 'respond-to-transfer-request-button',
                        title: intl('respondToRequest'),
                        onPress: () =>
                            navigation.navigate('transfer-request-response', {
                                transferProfileId: profileId,
                                transferRequestId: userRecipientTransferRequest.medicalProfileOwnershipTransferId,
                            }),
                        icon: 'UserCheck',
                    },
                ]}
            />
        );
    } else {
        return (
            <LargeAlertPaper
                icon="HorizontalArrows2"
                title={intl('observerTitle')}
                message={
                    profileTransferRequest.status === 'pending-collaborator-transfer-request'
                        ? intl('observerCollaboratorTransferMessage', {
                              fromUserFullName: getName(profileTransferRequest.transferFromUser),
                              toUserFullName: getName(profileTransferRequest.transferToUser),
                          })
                        : intl('observerNonCollaboratorTransferMessage', {
                              fromUserFullName: getName(profileTransferRequest.transferFromUser),
                              toUserEmail: profileTransferRequest.transferToEmail,
                          })
                }
            />
        );
    }
};

export default React.memo(PendingOwnershipTransferAlert);
